import React from "react";
import "@/index.css";
import { ThemeProvider } from "next-themes";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { GoogleOAuthProvider } from "@react-oauth/google";
import { AuthProvider } from "@/lib/auth";
import { Toaster } from "@/components/ui/sonner";
import Landing from "@/pages/Landing";
import Login from "@/pages/Login";
import Signup from "@/pages/Signup";
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";
import Dashboard from "@/pages/Dashboard";
import Filing from "@/pages/Filing";
import TaxCalculator from "@/pages/TaxCalculator";
import Documents from "@/pages/Documents";
import AIChat from "@/pages/AIChat";
import Pricing from "@/pages/Pricing";
import Profile from "@/pages/Profile";
import { Knowledge, Article } from "@/pages/Knowledge";
import Appointments from "@/pages/Appointments";
import CAPortal from "@/pages/CAPortal";
import AdminPanel from "@/pages/AdminPanel";
import { About, Contact } from "@/pages/Static";
import Notifications from "@/pages/Notifications";
import CAFilingFlow from "@/pages/CAFilingFlow";
import AuditTrail from "@/pages/AuditTrail";
import Roadmap from "@/pages/Roadmap";
import Practice from "@/pages/Practice";
import AISIngestion from "@/pages/AISIngestion";
import TDS from "@/pages/TDS";
import Notices from "@/pages/Notices";
import Calendar from "@/pages/Calendar";
import Approvals from "@/pages/Approvals";
import Plans from "@/pages/Plans";
import AdminGovernance from "@/pages/AdminGovernance";
import AdminFeatureFlags from "@/pages/AdminFeatureFlags";
import ProtectedRoute from "@/components/ProtectedRoute";

const GOOGLE_CLIENT_ID = process.env.REACT_APP_GOOGLE_CLIENT_ID || "";

function App() {
  return (
    <GoogleOAuthProvider clientId={GOOGLE_CLIENT_ID}>
    <ThemeProvider attribute="class" defaultTheme="light" enableSystem={false} storageKey="ca-ritika-theme" disableTransitionOnChange>
      <AuthProvider>
        <BrowserRouter>
          <Routes>
            <Route path="/" element={<Landing />} />
            <Route path="/login" element={<Login />} />
            <Route path="/signup" element={<Signup />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route path="/pricing" element={<Pricing />} />
            <Route path="/calculator" element={<TaxCalculator />} />
            <Route path="/knowledge" element={<Knowledge />} />
            <Route path="/knowledge/:slug" element={<Article />} />
            <Route path="/about" element={<About />} />
            <Route path="/contact" element={<Contact />} />
            <Route path="/roadmap" element={<Roadmap />} />

            <Route path="/dashboard" element={<ProtectedRoute roles={["taxpayer"]}><Dashboard /></ProtectedRoute>} />
            <Route path="/filing" element={<ProtectedRoute roles={["taxpayer"]}><Filing /></ProtectedRoute>} />
            <Route path="/documents" element={<ProtectedRoute roles={["taxpayer"]}><Documents /></ProtectedRoute>} />
            <Route path="/ai" element={<ProtectedRoute><AIChat /></ProtectedRoute>} />
            <Route path="/appointments" element={<ProtectedRoute><Appointments /></ProtectedRoute>} />
            <Route path="/profile" element={<ProtectedRoute><Profile /></ProtectedRoute>} />
            <Route path="/notifications" element={<ProtectedRoute><Notifications /></ProtectedRoute>} />
            <Route path="/ca-flow/:filingId" element={<ProtectedRoute roles={["taxpayer"]}><CAFilingFlow /></ProtectedRoute>} />

            <Route path="/ca-portal/*" element={<ProtectedRoute roles={["ca", "admin"]}><CAPortal /></ProtectedRoute>} />
            <Route path="/practice/*" element={<ProtectedRoute roles={["ca", "admin"]}><Practice /></ProtectedRoute>} />
            <Route path="/ais" element={<ProtectedRoute roles={["ca", "admin", "taxpayer"]}><AISIngestion /></ProtectedRoute>} />
            <Route path="/tds/*" element={<ProtectedRoute roles={["ca", "admin"]}><TDS /></ProtectedRoute>} />
            <Route path="/notices/*" element={<ProtectedRoute roles={["ca", "admin", "taxpayer"]}><Notices /></ProtectedRoute>} />
            <Route path="/calendar" element={<ProtectedRoute roles={["ca", "admin", "taxpayer"]}><Calendar /></ProtectedRoute>} />
            <Route path="/approvals/*" element={<ProtectedRoute roles={["ca", "admin"]}><Approvals /></ProtectedRoute>} />
            <Route path="/plans" element={<ProtectedRoute roles={["ca", "admin", "taxpayer"]}><Plans /></ProtectedRoute>} />
            <Route path="/admin/*" element={<ProtectedRoute roles={["admin"]}><AdminPanel /></ProtectedRoute>} />
            <Route path="/admin-governance" element={<ProtectedRoute roles={["admin"]}><AdminGovernance /></ProtectedRoute>} />
            <Route path="/admin-flags" element={<ProtectedRoute roles={["admin"]}><AdminFeatureFlags /></ProtectedRoute>} />
            <Route path="/audit" element={<ProtectedRoute roles={["admin", "ca"]}><AuditTrail /></ProtectedRoute>} />

            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
          <Toaster richColors position="top-right" />
        </BrowserRouter>
      </AuthProvider>
    </ThemeProvider>
    </GoogleOAuthProvider>
  );
}

export default App;
